import { Controller, useForm } from 'react-hook-form';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Message } from 'semantic-ui-react';
import { Form } from 'components/form-elements/Form';
import { LabeledInput } from 'components/form-elements/inputs/LabeledInput';
import { LabeledSelect } from 'components/form-elements/selects/LabeledSelect';
import { ShouldRender } from 'components/ShouldRender';
import { Grid } from 'components/layout/Grid';
import { Brikk } from 'components/Brikk';
import { Text } from 'components/typography';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUpdateAccessRule } from 'services/2n/access-rules';
import { useGetGroups } from 'services/2n/groups';
import { useGetTimeProfiles } from 'services/2n/time-profiles';
import { useGetZones } from 'services/2n/zones';
import { useModal } from 'store/modals';
import { useAccessManagement2NModal } from '../../../AccessManagement2NModalContext/useAccessManagement2NModal';
import { DeleteAccessRule } from '../DeleteAccessRule';
import { discardChanges } from '../../../../discardChanges';
import accessRuleFormSchema from './validation';
import styles from './styles.module.scss';

function createOption(value, label) {
  if (!value) return null;

  return {
    value,
    label: label || String(value),
  };
}

function findOptionByValue(options, value, fallbackLabel) {
  const option = (options || []).find(item => item.value === value);

  return option || createOption(value, fallbackLabel);
}

function buildAccessRuleName(groupOption, zoneOption) {
  return [
    groupOption && groupOption.label,
    zoneOption && zoneOption.label,
  ].filter(Boolean).join(' - ');
}

export function AccessRuleForm() {
  const modal = useModal();
  const accessManagement2NModal = useAccessManagement2NModal();
  const selectedAccessRule = accessManagement2NModal.selected.accessRule;
  const intl = useIntl();

  const { updateAccessRuleAsync, isLoading: isUpdating } = useUpdateAccessRule();
  const { groups } = useGetGroups();
  const { timeProfiles } = useGetTimeProfiles();
  const { zones } = useGetZones();

  const selectedAccessRuleId = (selectedAccessRule && selectedAccessRule.Id) || '';
  const selectedAccessRuleName = (selectedAccessRule && selectedAccessRule.Name) || '';
  const selectedAccessRuleGroupId = (selectedAccessRule && selectedAccessRule.Group
    && selectedAccessRule.Group.Id) || '';
  const selectedAccessRuleGroupName = (selectedAccessRule && selectedAccessRule.Group
    && selectedAccessRule.Group.Name) || '';
  const selectedAccessRuleZoneId = (selectedAccessRule && selectedAccessRule.Zone
    && selectedAccessRule.Zone.Id) || '';
  const selectedAccessRuleZoneName = (selectedAccessRule && selectedAccessRule.Zone
    && selectedAccessRule.Zone.Name) || '';
  const selectedAccessRuleTimeProfileId = (selectedAccessRule && selectedAccessRule.TimeProfile
    && selectedAccessRule.TimeProfile.Id) || '';
  const selectedAccessRuleTimeProfileName = (selectedAccessRule && selectedAccessRule.TimeProfile
    && selectedAccessRule.TimeProfile.Name) || '';

  const timeProfileOptions = (timeProfiles || []).map(timeProfile => ({
    value: timeProfile.Id || timeProfile.id,
    label: timeProfile.Name || timeProfile.name,
  }));

  const groupsInThisRule = (groups || []).map(group => ({
    value: group.Id || group.id,
    label: group.Name || group.name,
  }));

  const zoneOptions = (zones || []).map(zone => ({
    value: zone.Id || zone.id,
    label: zone.Name || zone.name,
  }));
  
  const defaultGroupOption = findOptionByValue(
    groupsInThisRule,
    selectedAccessRuleGroupId,
    selectedAccessRuleGroupName,
  );
  const defaultZoneOption = findOptionByValue(
    zoneOptions,
    selectedAccessRuleZoneId,
    selectedAccessRuleZoneName,
  );

  const defaultValues = {
    name: buildAccessRuleName(defaultGroupOption, defaultZoneOption) || selectedAccessRuleName,
    timeProfile: findOptionByValue(
      timeProfileOptions,
      selectedAccessRuleTimeProfileId,
      selectedAccessRuleTimeProfileName,
    ),
    zone: defaultZoneOption,
    group: defaultGroupOption,
  };

  const { control, handleSubmit, formState, reset, setValue, getValues } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(accessRuleFormSchema),
    defaultValues,
  });

  const { errors } = formState || {};

  useEffect(() => {
    if (!selectedAccessRule) return;
    reset({
      ...defaultValues,
    });
  }, [selectedAccessRule, groups, timeProfiles, zones]);
  
  const onSubmit = async (data) => {
    if (!selectedAccessRuleId) return;
    const generatedName = buildAccessRuleName(data.group, data.zone);

    await updateAccessRuleAsync({
      id: selectedAccessRuleId,
      name: data.name || generatedName,
      timeProfileId: data.timeProfile && data.timeProfile.value,
      zoneId: data.zone && data.zone.value,
      groupId: data.group && data.group.value,
    });
  };

  const formatError = (msg) => {
    if (!msg) return '';
    try {
      return typeof msg === 'string' ? intl.formatMessage({ id: msg }) : String(msg);
    } catch (e) {
      return String(msg);
    }
  };

  useEffect(() => {
    if (formState.isDirty) {
      accessManagement2NModal.updateSavedChanges('accessRule', formState.isDirty);
    }
  }, [formState.isDirty]);

  return (
    <div className={styles.container}>
      <Brikk.Heading heading="editAccessRule" />
      <ShouldRender condition={selectedAccessRule && Object.hasOwn(selectedAccessRule, 'name')}>
        <Text
          element="h2"
          thin
          xl
          size={20}
        >
          {String(selectedAccessRuleName || '')}
        </Text>
      </ShouldRender>
      {selectedAccessRule ? (
      
        <Form
          id="access-rule-form"
          className={styles.formContainer}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className={styles.formWrapper}>
            <Grid
              noPadding
              oneColumn
            >
              <div className={styles.form}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <LabeledInput
                      labelIntl="accessRuleName"
                      id="name"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      hasError={!!(errors && errors.name)}
                      disabled
                    />
                  )}
                />
                <ShouldRender condition={errors && errors.name}>
                  <Message negative>
                    <Message.Content>
                      <Text
                        error
                        intl={errors.name && errors.name.message}
                      />
                    </Message.Content>
                  </Message>
                </ShouldRender>

                <Controller
                  name="timeProfile"
                  control={control}
                  render={({ field }) => (
                    <LabeledSelect
                      title="timeProfile"
                      options={timeProfileOptions}
                      value={field.value}
                      onChange={field.onChange}
                      hasError={!!(errors && errors.timeProfile)}
                    />
                  )}
                />
                <ShouldRender condition={errors && errors.timeProfile}>
                  <Message negative>
                    <Message.Content>
                      <Text error>{formatError(errors.timeProfile && errors.timeProfile.message)}</Text>
                    </Message.Content>
                  </Message>
                </ShouldRender>

                <Controller
                  name="zone"
                  control={control}
                  render={({ field }) => (
                    <LabeledSelect
                      title="resourceName"
                      options={zoneOptions}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        setValue('name', buildAccessRuleName(getValues('group'), value), {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                      hasError={!!(errors && errors.zone)}
                    />
                  )}
                />
                <ShouldRender condition={errors && errors.group}>
                  <Message negative>
                    <Message.Content>
                      <Text
                        error
                        intl={errors.group && errors.group.message}
                      />
                    </Message.Content>
                  </Message>
                </ShouldRender>

                <Controller
                  name="group"
                  control={control}
                  render={({ field }) => (
                    <LabeledSelect
                      title="groupName"
                      options={groupsInThisRule}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        setValue('name', buildAccessRuleName(value, getValues('zone')), {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                      hasError={!!(errors && errors.group)}
                    />
                  )}
                />
                <ShouldRender condition={errors && errors.name}>
                  <Message negative>
                    <Message.Content>
                      <Text
                        error
                        intl={errors.name && errors.name.message}
                      />
                    </Message.Content>
                  </Message>
                </ShouldRender>

              </div>
            </Grid>
          </div>

          <div className={styles.formButtons}>
            <Button
              basic
              name="cancel-access-rule-details"
              type="button"
              disabled={isUpdating}
              onClick={() => discardChanges('accessRule', null, intl, accessManagement2NModal, modal)}
            >
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              type="submit"
              name="submit-access-rule-details"
              disabled={isUpdating}
              loading={isUpdating}
            >
              <FormattedMessage id="saveChanges" />
            </Button>
          </div>
          <ShouldRender condition={selectedAccessRule}>
            <DeleteAccessRule />
          </ShouldRender>
        </Form>
       
      ) : (
        <Text
          secondary
          center
          intl="noAccessRuleSelected"
          size={16}
        />
      )}
    </div>
  );
}

